import { getResponseError, hasResponseError } from '@/helpers/apiHelpers/responseHelpers';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { amplitudeService } from '@/services/amplitudeService/amplitudeService';
import { AMPLITUDE_EVENTS } from '@/services/amplitudeService/amplitudeEvents';
import { useParams } from 'react-router';
import { CREATE_ORGANIZATION_USER_VALIDATION_SCHEMA, getCreateOrganizationUserInitialValues } from './const';
import { useFormik } from 'formik';
import { ADDING_ORG_ADMIN_ROLE_STATUS_MAP } from '@/api/v1/opco/organizations/getUserAddingStatusApi';
import { Notification } from '@/components/Notification/Notification';
import { FormikForm } from '@/components/form/FormikForm/FormikForm';
import { Box } from '@/components/Box/Box';
import { Button } from '@/components/Button/Button';
import { CheckPinCodeModal } from '@/bundle/shared/components/CheckPinCodeModal/CheckPinCodeModal';
import { PIN_TOKEN_EVENT_SCOPE } from '@/const/shared';
import { FormikInput } from '@/components/form/fields/FormikInput/FormikInput';
import { FormikPhoneInput } from '@/components/form/fields/FormikPhoneInput/FormikPhoneInput';
import { CreateOrgAdminInput } from '../../component/CreateOrgAdminInput';
import { useAssignUser } from '@/bundle/shared/hooks/useAssignUser';
import { OrganizationUserType } from '@/bundle/_Opco/types/types';
import { HTTP_BAD_REQUEST_STATUS } from '@/api/const';
import { FormikPanelRadioGroup } from '@/components/form/fields/FormikPanelRadioGroup/FormikPanelRadioGroup';
import { SELECT_ORGANIZATION_ADMIN_ROLE_RADIO_OPTIONS } from '@/const/formConfigs';
import {
  addedUserAssignmentStatus,
  addingOrgAdminRole,
  createOrganizationAdmin,
  loadAddedUserDetails,
} from '@/bundle/_Opco/ui/OrganizationDetails/api/api';
import { ORG_ADMIN_ROLE } from '@/const/user';

interface CreateOrganizationAdminFormType {
  hasUsers: boolean;
  onClose: () => void;
  onSuccess: () => void;
  hasSubscription: boolean;
  onOpenInitialUsersLimitModal: () => void;
}

const INITIAL_USERS_LIMIT_REACHED_ERROR_MESSAGE = 'Unpurchased subscription master admin users limit exceeded.';

export const CreateOrganizationAdminForm = ({
  hasUsers,
  onClose,
  onSuccess,
  hasSubscription,
  onOpenInitialUsersLimitModal,
}: CreateOrganizationAdminFormType) => {
  const { id } = useParams<{ id: string }>();
  const [isPinCodeOpen, setIsPinCodeOpen] = useState(false);
  const [submittedEmailRequest, setSubmittedEmailRequest] = useState(false);
  const [assignedUserId, setAssignedUserId] = useState<null | string>(null);
  const [roleOptions, setRoleOptions] = useState(SELECT_ORGANIZATION_ADMIN_ROLE_RADIO_OPTIONS);
  const canCreateOrgAdminRole = hasUsers && hasSubscription;

  const validationSchema = assignedUserId ? null : CREATE_ORGANIZATION_USER_VALIDATION_SCHEMA;

  const { data: addingUserStatusData } = useQuery({
    queryKey: ['adding_user_status', assignedUserId],
    queryFn: () => {
      return addedUserAssignmentStatus(assignedUserId);
    },
    enabled: !!assignedUserId,
  });

  const { data: loadUserData } = useQuery({
    queryKey: ['load_user_details', assignedUserId],
    queryFn: () => loadAddedUserDetails(assignedUserId),
    enabled: !!assignedUserId && !!addingUserStatusData?.body?.is_available_to_add,
  });

  const formik = useFormik({
    initialValues: getCreateOrganizationUserInitialValues(canCreateOrgAdminRole),
    validationSchema,
    onSubmit: () => {
      if (addingUserStatusData?.body?.code === ADDING_ORG_ADMIN_ROLE_STATUS_MAP.CAN_BE_ADDED) {
        amplitudeService.logEvent(AMPLITUDE_EVENTS.CreateOrgAdminExistingRedirect);
      } else {
        amplitudeService.logEvent(AMPLITUDE_EVENTS.CreateOrgAdminNewRedirect);
      }

      setIsPinCodeOpen(true);
    },
  });

  const {
    mutate: createOrganizationAdminMutate,
    data: createOrganizationAdminData,
    isPending: isPendingCreateOrganizationAdmin,
    reset: resetCreateOrganizationAdmin,
  } = useMutation({
    mutationKey: ['create_org_admin'],
    mutationFn: (pinCode: string) => {
      return createOrganizationAdmin(id, formik.values, pinCode);
    },
    onSuccess(response) {
      if (response.error) {
        const hasError = hasResponseError(response?.error, INITIAL_USERS_LIMIT_REACHED_ERROR_MESSAGE);

        if (hasError) {
          onClose();
          onOpenInitialUsersLimitModal();
        }

        return;
      }

      amplitudeService.logEvent(AMPLITUDE_EVENTS.CreateOrgAdminNewSuccess);
      onSuccess();
    },
  });

  const {
    mutate: addingOrgAdminRoleMutate,
    data: addingOrgAdminRoleData,
    isPending: isPendingAddingOrgAdminRole,
    reset: resetAddingOrgAdminRole,
  } = useMutation({
    mutationKey: ['adding_org_admin_role'],
    mutationFn: (pinCode: string) => {
      const payload = {
        user_id: assignedUserId,
        role: formik.values.role,
      };

      return addingOrgAdminRole(id, payload, pinCode);
    },
    onSuccess(response) {
      if (response.error) {
        const hasError = hasResponseError(response?.error, INITIAL_USERS_LIMIT_REACHED_ERROR_MESSAGE);

        if (hasError) {
          onClose();
          onOpenInitialUsersLimitModal();
        }

        return;
      }

      amplitudeService.logEvent(AMPLITUDE_EVENTS.CreateOrgAdminExistingSuccess);

      onSuccess();
    },
  });

  useEffect(() => {
    if (hasUsers && hasSubscription) return;

    // Set only Master Admin option for empty organization and when have subscription without one master admin
    const masterAdminOption = SELECT_ORGANIZATION_ADMIN_ROLE_RADIO_OPTIONS.filter(
      (option) => option.value === ORG_ADMIN_ROLE.MASTER_ADMIN
    );

    setRoleOptions(masterAdminOption);
  }, [hasUsers, hasSubscription]);

  const closePinCode = () => {
    setIsPinCodeOpen(false);
  };

  const checkPinCodeSuccess = (pinToken: string) => {
    if (assignedUserId) {
      addingOrgAdminRoleMutate(pinToken);
      closePinCode();

      return;
    }

    createOrganizationAdminMutate(pinToken);
    closePinCode();
  };

  const searchUserEmail = (userId: string) => {
    formik.setErrors({});
    setAssignedUserId(userId);
    setSubmittedEmailRequest(true);
  };

  const submitUserEmail = () => {
    if (!formik.touched.email && formik.errors?.email) {
      formik.setTouched({ email: true });
    }
  };

  const onResetData = () => {
    setAssignedUserId(null);
    setSubmittedEmailRequest(false);
    resetCreateOrganizationAdmin();
    resetAddingOrgAdminRole();
    formik.resetForm({
      values: {
        ...getCreateOrganizationUserInitialValues(hasUsers),
        role: formik.values.role,
      },
    });
    formik.setFieldValue('email', formik.values.email);
  };

  const apiError = createOrganizationAdminData?.error || addingOrgAdminRoleData?.error;
  const hasApiError = apiError?.statusCode === HTTP_BAD_REQUEST_STATUS;

  useAssignUser<OrganizationUserType>(
    loadUserData?.body,
    formik,
    onResetData,
    assignedUserId,
    submittedEmailRequest,
    addingUserStatusData?.body?.is_available_to_add,
    apiError
  );

  const addingUserStatus = addingUserStatusData?.body?.code;
  const isLoading = isPendingAddingOrgAdminRole || isPendingCreateOrganizationAdmin;
  const currentEventScope = assignedUserId
    ? PIN_TOKEN_EVENT_SCOPE.ADD_ORG_USER_ROLE
    : PIN_TOKEN_EVENT_SCOPE.CREATE_ORG_ADMIN;

  const isNotAvailableToAssignUser = addingUserStatusData?.body && !addingUserStatusData?.body?.is_available_to_add;
  const formError = getResponseError(apiError);
  const isDisabledForm = !!assignedUserId || !submittedEmailRequest || hasApiError;
  const isDisabledSave = isNotAvailableToAssignUser || !submittedEmailRequest || hasApiError;

  useEffect(() => {
    if (isNotAvailableToAssignUser) {
      amplitudeService.logEvent(AMPLITUDE_EVENTS.CreateOrgAdminFailed);
    }
  }, [isNotAvailableToAssignUser]);

  return (
    <>
      {formError && (
        <Notification variant='error' marginBottom='24px'>
          {formError}
        </Notification>
      )}

      <FormikForm value={formik}>
        <FormikPanelRadioGroup name='role' label='Role*' options={roleOptions} disabled={!canCreateOrgAdminRole} />
        <CreateOrgAdminInput
          email={formik.values.email}
          assignmentStatus={addingUserStatus}
          onSearch={searchUserEmail}
          onSubmit={submitUserEmail}
          error={formik.errors.email}
          apiError={apiError}
        />

        <FormikPhoneInput
          name='mobile_phone'
          label='Mobile Phone Number*'
          placeholder='Enter Mobile Phone Number With Country Code'
          apiError={apiError}
          disabled={isDisabledForm}
        />

        <FormikInput
          name='first_name'
          label='First Name*'
          placeholder='Enter First Name'
          apiError={apiError}
          disabled={isDisabledForm}
        />
        <FormikInput
          name='middle_name'
          label='Middle Name'
          placeholder='Enter Middle Name'
          apiError={apiError}
          disabled={isDisabledForm}
        />
        <FormikInput
          name='last_name'
          label='Last Name*'
          placeholder='Enter Last Name'
          apiError={apiError}
          disabled={isDisabledForm}
        />

        <Box display='flex' alignItems='center' columnGap='20px' marginTop='14px'>
          <Button variant='secondary' onClick={onClose}>
            Cancel
          </Button>
          <Button type='submit' isLoading={isLoading} disabled={isDisabledSave}>
            Save
          </Button>
        </Box>
      </FormikForm>
      {isPinCodeOpen && (
        <CheckPinCodeModal
          isOpen={isPinCodeOpen}
          onSuccess={checkPinCodeSuccess}
          onClose={closePinCode}
          eventScope={currentEventScope}
        />
      )}
    </>
  );
};
